/**
 * The Exedra: a rectangular gallery whose far end opens through a trabeated
 * screen into a semicircular apse. The apse is faceted into five to seven flat
 * bays — each one a hero wall — under a half-dome lit by a cove band at the
 * springing. The straight walls hang as usual; the wall the apse replaces
 * drops out of the hang entirely.
 */

import { PALACE, SIDES } from '../constants';
import { rectSegments } from '../hang';

const OPEN_H = 4.3; // head of the apse opening; the half-dome springs here
const SETBACK = 0.12; // air between the deepest bay and the wall behind it
const BAY_MARGIN = 0.22; // dead margin at each end of a bay's hanging face

// Inward face normals, and the sphere/cylinder start angles that pick out the
// half facing each side (three.js: sphere phi 0 → −x, π/2 → +z; cylinder
// theta 0 → +z, π/2 → +x).
const FACE = {
  N: { nx: 0, nz: -1, out: Math.PI / 2, phi: 0, theta: -Math.PI / 2 },
  S: { nx: 0, nz: 1, out: -Math.PI / 2, phi: Math.PI, theta: Math.PI / 2 },
  E: { nx: -1, nz: 0, out: 0, phi: Math.PI / 2, theta: 0 },
  W: { nx: 1, nz: 0, out: Math.PI, phi: -Math.PI / 2, theta: Math.PI },
};

// Door-free sides, best first: opposite the entry, then the ends of the room's
// long axis (the classical place for an exedra), then whatever is left.
const apseCandidates = (node) => {
  const doors = node.doors || [];
  const used = new Set(doors.map((d) => d.side));
  const ends = node.x2 - node.x1 >= node.z2 - node.z1 ? ['E', 'W'] : ['N', 'S'];
  const opposite = doors.length ? SIDES[doors[0].side].opp : null;
  const rank = (s) => (s === opposite ? 0 : ends.includes(s) ? 1 : 2);
  return ['N', 'S', 'E', 'W'].filter((s) => !used.has(s)).sort((a, b) => rank(a) - rank(b));
};

/**
 * The apse on one given side, or null when it cannot fit there without
 * swallowing a door or shrinking to a niche.
 */
const apseOn = (node, side) => {
  const { WALL_T } = PALACE;
  const xI1 = node.x1 + WALL_T;
  const xI2 = node.x2 - WALL_T;
  const zI1 = node.z1 + WALL_T;
  const zI2 = node.z2 - WALL_T;
  const horiz = side === 'N' || side === 'S'; // chord runs along x
  const sgn = side === 'N' || side === 'E' ? 1 : -1;
  const face = side === 'N' ? zI2 : side === 'S' ? zI1 : side === 'E' ? xI2 : xI1;
  const span = horiz ? xI2 - xI1 : zI2 - zI1;
  const depth = horiz ? zI2 - zI1 : xI2 - xI1;

  const want = Math.min(7, Math.max(5, node.params?.bays ?? 6));
  const half = Math.PI / (2 * want);

  // A door on either flanking wall must stay in the room proper, never behind
  // the chord: the apse gives up depth rather than seal a doorway off.
  let doorLimit = Infinity;
  for (const door of node.doors || []) {
    const flanking = horiz ? door.side === 'E' || door.side === 'W'
      : door.side === 'N' || door.side === 'S';
    if (!flanking) continue;
    const edge = door.at + sgn * ((door.w || PALACE.DOOR_W) / 2 + 0.5);
    doorLimit = Math.min(doorLimit, Math.abs(face - edge) - SETBACK);
  }

  // Bay faces are tangent to the apse circle, so the half-dome springs into
  // the wall rather than off its edge; the vertices sit a touch further out.
  const r = Math.min(
    node.params?.apseR ?? 3.3,
    (span / 2 - 0.5) * Math.cos(half),
    depth * 0.42,
    doorLimit
  );
  if (r < 2) return null;
  // Drop bays until each facet is wide enough to be worth hanging a picture on
  // (fewer bays only widen the tangent polygon's clearance, so r still fits).
  let bays = want;
  while (bays > 5 && 2 * r * Math.tan(Math.PI / (2 * bays)) < 1.7) bays--;
  const step = Math.PI / bays;
  const rv = r / Math.cos(step / 2);
  const chord = face - sgn * (r + SETBACK);
  const mid = horiz ? (node.x1 + node.x2) / 2 : (node.z1 + node.z2) / 2;
  const acx = horiz ? mid : chord;
  const acz = horiz ? chord : mid;

  const start = FACE[side].out - Math.PI / 2;
  const verts = [];
  for (let i = 0; i <= bays; i++) {
    const a = start + i * step;
    verts.push([acx + rv * Math.cos(a), acz + rv * Math.sin(a)]);
  }
  return {
    side, horiz, sgn, r, rv, bays, step, start, chord, mid, acx, acz, verts,
    xI1, xI2, zI1, zI2,
  };
};

/**
 * Everything segments() and build() must agree on: the first candidate side
 * that can actually take the apse, and its geometry. Pure — all randomness is
 * baked into node.params by plan(). Null means no side works and the room
 * stays a plain rectangular gallery.
 */
const apseGeom = (node) => {
  for (const side of apseCandidates(node)) {
    const g = apseOn(node, side);
    if (g) return g;
  }
  return null;
};

// Trim a straight run back to the room proper when it heads into the pocket
// behind the apse. Runs crossing the apse axis are left alone.
const clipToRoom = (seg, g) => {
  const runsIn = g.horiz ? seg.dz === 1 : seg.dx === 1;
  if (!runsIn) return seg;
  const limit = g.chord - g.sgn * 0.35;
  let a = g.horiz ? seg.oz : seg.ox;
  let b = a + seg.len;
  if (g.sgn > 0) b = Math.min(b, limit);
  else a = Math.max(a, limit);
  if (b - a < 1.2) return null;
  return {
    ...seg,
    ox: g.horiz ? seg.ox : a,
    oz: g.horiz ? a : seg.oz,
    len: b - a,
    style: b - a < 3.2 ? 'hero' : seg.style,
  };
};

const exedra = {
  id: 'exedra',
  role: 'wing',
  weight: 2,

  plan(rand, { axis }) {
    const long = 10.5 + rand() * 2.5;
    const cross = 9 + rand() * 1.8;
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 7,
      params: {
        bays: 5 + Math.floor(rand() * 3),
        apseR: 3 + rand() * 0.8,
      },
    };
  },

  segments(node) {
    const base = rectSegments(node, { longStyle: 'cluster', shortStyle: 'cluster' });
    const g = apseGeom(node);
    if (!g) return base;

    const f = FACE[g.side];
    const { WALL_T, SEG_MARGIN } = PALACE;
    const out = [];
    for (const seg of base) {
      if (seg.nx === f.nx && seg.nz === f.nz) continue; // the wall the apse replaces
      const clipped = clipToRoom(seg, g);
      if (clipped) out.push(clipped);
    }

    // The two returns flanking the apse opening, on the chord line.
    const faceCoord = g.chord - g.sgn * WALL_T / 2;
    const lo = g.horiz ? g.xI1 : g.zI1;
    const hi = g.horiz ? g.xI2 : g.zI2;
    for (const run of [[lo + SEG_MARGIN, g.mid - g.rv - 0.2], [g.mid + g.rv + 0.2, hi - SEG_MARGIN]]) {
      const len = run[1] - run[0];
      if (len < 1.2) continue;
      out.push({
        ox: g.horiz ? run[0] : faceCoord,
        oz: g.horiz ? faceCoord : run[0],
        dx: g.horiz ? 1 : 0,
        dz: g.horiz ? 0 : 1,
        nx: f.nx,
        nz: f.nz,
        len,
        style: len < 3.2 ? 'hero' : 'cluster',
      });
    }

    // One hero per faceted bay, on the bay's flat face.
    for (let i = 0; i < g.bays; i++) {
      const a = g.verts[i];
      const b = g.verts[i + 1];
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const usable = len - 2 * BAY_MARGIN;
      if (usable < 0.8) continue;
      const ux = (b[0] - a[0]) / len;
      const uz = (b[1] - a[1]) / len;
      const mA = g.start + (i + 0.5) * g.step; // outward normal of this bay
      const nx = -Math.cos(mA);
      const nz = -Math.sin(mA);
      out.push({
        ox: a[0] + ux * BAY_MARGIN + nx * WALL_T / 2,
        oz: a[1] + uz * BAY_MARGIN + nz * WALL_T / 2,
        dx: ux,
        dz: uz,
        nx,
        nz,
        len: usable,
        y0: 0.85,
        style: 'hero',
      });
    }
    return out.sort((a, b) => b.len - a.len);
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const M = T.kit.materials;
    const height = node.height;
    const g = apseGeom(node);
    const { group, colliders } = T.standardShell(node, T.kit, { laylight: !g });
    if (!g) return { group, colliders };

    const wallMat = T.kit.wallFor(node.palette || 0);
    const xI1 = g.xI1;
    const xI2 = g.xI2;
    const zI1 = g.zI1;
    const zI2 = g.zI2;

    // --- the chord wall: two returns closing the pockets either side of the
    // apse, and a screen over the opening between them.
    const walls = [];
    const half = WALL_T / 2;
    const rect = (a, b, y0, y1) => (g.horiz
      ? T.box(a, b, y0, y1, g.chord - half, g.chord + half)
      : T.box(g.chord - half, g.chord + half, y0, y1, a, b));
    const lo = g.horiz ? xI1 : zI1;
    const hi = g.horiz ? xI2 : zI2;
    for (const run of [[lo, g.mid - g.rv], [g.mid + g.rv, hi]]) {
      if (run[1] - run[0] < 0.05) continue;
      walls.push(rect(run[0], run[1], 0, height));
      colliders.push({
        rect: g.horiz
          ? [run[0], g.chord - half, run[1], g.chord + half]
          : [g.chord - half, run[0], g.chord + half, run[1]],
        top: height,
      });
    }
    walls.push(rect(g.mid - g.rv, g.mid + g.rv, OPEN_H, height));

    // --- the faceted bays.
    for (let i = 0; i < g.bays; i++) {
      const a = g.verts[i];
      const b = g.verts[i + 1];
      const w = T.angledWall(a[0], a[1], b[0], b[1], OPEN_H);
      walls.push(w.geo);
      colliders.push(w.collider);
    }
    group.add(T.mergedMesh(walls, wallMat));

    // --- half-dome over the apse, squashed to sit under the ceiling.
    const rise = Math.min(g.r, height - OPEN_H - 0.35);
    const dome = new T.THREE.SphereGeometry(
      g.r, 20, 10, FACE[g.side].phi, Math.PI, 0, Math.PI / 2
    );
    dome.scale(1, rise / g.r, 1);
    dome.translate(g.acx, OPEN_H, g.acz);
    group.add(T.mergedMesh([dome], M.dome));

    // A cove band just under the springing: the apse lights its own bays.
    const cove = new T.THREE.Mesh(
      new T.THREE.CylinderGeometry(g.r - 0.26, g.r - 0.26, 0.32, 16, 1, true, FACE[g.side].theta, Math.PI),
      M.laylight
    );
    cove.position.set(g.acx, OPEN_H - 0.26, g.acz);
    cove.matrixAutoUpdate = false;
    cove.updateMatrix();
    group.add(cove);

    // --- laylight over the room proper, pushed off-centre away from the apse.
    const roomLo = g.sgn > 0 ? (g.horiz ? zI1 : xI1) : g.chord;
    const roomHi = g.sgn > 0 ? g.chord : (g.horiz ? zI2 : xI2);
    const across = (g.horiz ? xI2 - xI1 : zI2 - zI1) - 3.4;
    const along = roomHi - roomLo - 2.6;
    const lcx = g.horiz ? (xI1 + xI2) / 2 : (roomLo + roomHi) / 2;
    const lcz = g.horiz ? (roomLo + roomHi) / 2 : (zI1 + zI2) / 2;
    const layW = Math.max(2, g.horiz ? across : along);
    const layD = Math.max(2, g.horiz ? along : across);
    const BEAM = 0.14;
    const lx1 = lcx - layW / 2;
    const lx2 = lcx + layW / 2;
    const lz1 = lcz - layD / 2;
    const lz2 = lcz + layD / 2;
    const trim = [
      T.box(lx1 - BEAM, lx2 + BEAM, height - 0.15, height, lz1 - BEAM, lz1),
      T.box(lx1 - BEAM, lx2 + BEAM, height - 0.15, height, lz2, lz2 + BEAM),
      T.box(lx1 - BEAM, lx1, height - 0.15, height, lz1, lz2),
      T.box(lx2, lx2 + BEAM, height - 0.15, height, lz1, lz2),
    ];
    const sky = new T.THREE.Mesh(T.kit.geometries.unitPlane, M.laylight);
    sky.rotation.x = Math.PI / 2;
    sky.scale.set(layW, layD, 1);
    sky.position.set(lcx, height - 0.02, lcz);
    sky.matrixAutoUpdate = false;
    sky.updateMatrix();
    group.add(sky);

    // Impost band across the screen, so the opening reads as an order.
    const bandP = 0.07;
    const bf1 = g.chord - g.sgn * (half + bandP);
    const bf2 = g.chord - g.sgn * (half - 0.03);
    trim.push(g.horiz
      ? T.box(g.mid - g.rv - 0.2, g.mid + g.rv + 0.2, OPEN_H - 0.2, OPEN_H, bf1, bf2)
      : T.box(bf1, bf2, OPEN_H - 0.2, OPEN_H, g.mid - g.rv - 0.2, g.mid + g.rv + 0.2));
    group.add(T.mergedMesh(trim, M.trim));

    // A bench mid-room, square on to the apse.
    const b = T.bench(T.kit, lcx, lcz, g.horiz ? 0 : Math.PI / 2, 2.4);
    group.add(b.mesh);
    colliders.push(b.collider);

    return { group, colliders };
  },
};

export default exedra;
